import { api } from './api';
import type {
  User,
  ApiResponse,
  LoginCredentials,
  SignupCredentials,
} from '@soundbyte/types';

// Response type that includes session token for Safari/iOS fallback
interface AuthResponse {
  user: User;
  sessionToken?: string;
}

/**
 * Auth Service
 *
 * Handles all authentication-related operations using session-based auth.
 * Sessions are managed via HTTP-only cookies set by the backend.
 * Falls back to bearer token auth for Safari/iOS where cookies are blocked.
 */
class AuthService {
  /**
   * Initialize auth by checking if user has an active session
   * Returns the user if authenticated, null if not authenticated,
   * or throws an error if the check could not be completed
   */
  async initialize(): Promise<User | null> {
    const response = await api.get<User>('/auth/me');

    // The /auth/me endpoint returns the user object directly as data
    // (via sendSuccess(res, user)), not wrapped in { user: ... }
    if (response.success && response.data?.email) {
      return response.data;
    }

    // Also handle the case where it's wrapped in { user: ... }
    // (for backwards compatibility with any endpoint variations)
    const wrapped = response.data as any;
    if (response.success && wrapped?.user?.email) {
      return wrapped.user;
    }

    return null;
  }

  /**
   * Login with email and password
   * Stores session token for Safari/iOS fallback
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User }>> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);

    // Store session token for Safari/iOS fallback
    if (response.success && response.data?.sessionToken) {
      api.setSessionToken(response.data.sessionToken);
    }

    return response as ApiResponse<{ user: User }>;
  }

  /**
   * Sign up new user
   * Stores session token for Safari/iOS fallback
   */
  async signup(credentials: SignupCredentials): Promise<ApiResponse<{ user: User }>> {
    const response = await api.post<AuthResponse>('/auth/register', {
      email: credentials.email,
      password: credentials.password,
      fullName: credentials.fullName,
      confirmPassword: credentials.password, // Backend expects confirmPassword
    });

    // Store session token for Safari/iOS fallback
    if (response.success && response.data?.sessionToken) {
      api.setSessionToken(response.data.sessionToken);
    }

    return response as ApiResponse<{ user: User }>;
  }

  /**
   * Logout current user
   * Clears session token
   */
  async logout(): Promise<void> {
    // Include token in logout request so server can revoke it
    const token = api.getSessionToken();
    await api.post('/auth/logout', token ? { sessionToken: token } : undefined);

    // Clear local token
    api.clearSessionToken();
  }

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/forgot-password', { email });
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/reset-password', {
      token,
      password: newPassword,
      confirmPassword: newPassword,
    });
  }

  /**
   * Update user password (authenticated)
   */
  async updatePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return api.patch<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword: newPassword,
    });
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/verify-email', { token });
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/resend-verification', { email });
  }

  /**
   * Send verification code (6-digit code)
   */
  async sendVerificationCode(email: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/send-verification-code', { email });
  }

  /**
   * Verify email with code
   */
  async verifyEmailWithCode(email: string, code: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/verify-code', { email, code });
  }

  /**
   * Fetch a server-issued, single-use nonce bound to this browser session.
   * Pass the returned value to Google Identity Services (`nonce` init option)
   * so the backend can verify the ID token against it and prevent replay.
   */
  async getGoogleNonce(): Promise<ApiResponse<{ nonce: string }>> {
    return api.get<{ nonce: string }>('/auth/google/nonce');
  }

  /**
   * Sign in with Google
   * Stores session token for Safari/iOS fallback
   */
  async signInWithGoogle(idToken: string, nonce?: string): Promise<ApiResponse<{ user: User; isNewUser: boolean; message: string }>> {
    const response = await api.post<{ user: User; sessionToken?: string; isNewUser: boolean; message: string }>(
      '/auth/google',
      nonce ? { idToken, nonce } : { idToken }
    );

    // Store session token for Safari/iOS fallback
    if (response.success && response.data?.sessionToken) {
      api.setSessionToken(response.data.sessionToken);
    }

    return response as ApiResponse<{ user: User; isNewUser: boolean; message: string }>;
  }

  /**
   * Link Google account to current user
   */
  async linkGoogleAccount(idToken: string, nonce?: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/google/link', nonce ? { idToken, nonce } : { idToken });
  }

  /**
   * Unlink Google account from current user
   */
  async unlinkGoogleAccount(): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>('/auth/google/unlink');
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse<{ user: User }>> {
    return api.get<{ user: User }>('/users/profile');
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: Partial<Pick<User, 'fullName' | 'avatarUrl'>>): Promise<ApiResponse<{ user: User }>> {
    return api.patch<{ user: User }>('/users/profile', updates);
  }

  /**
   * Update user preferences (e.g., email notifications)
   */
  async updatePreferences(prefs: { emailNotifications: boolean }): Promise<ApiResponse<{ user: User }>> {
    return api.patch<{ user: User }>('/auth/preferences', prefs);
  }
}

export const authService = new AuthService();
